import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import deleteIcon from '/src/assets/delete.svg';
import upArrow from '../../assets/sort-arrow.svg';
import { Company } from '../../services/types';
import { generatePageNumbers } from '../../utils/helpers';

const STORAGE_KEY = 'companyTableState';

interface TableState {
  sortConfig: { key: string; direction: 'asc' | 'desc' | null };
  currentPage: number;
  selectedRows: number[];
}

interface CompanyTableProps {
    data: Company[];
    handleSelectRows: (selectedRows: number[]) => void;
}

const CompanyTable = ({ data, handleSelectRows }: CompanyTableProps) => {
    const [sortConfig, setSortConfig] = useState<TableState['sortConfig']>(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved).sortConfig : { key: '', direction: null };
    });

    const [currentPage, setCurrentPage] = useState<number>(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved).currentPage : 1;
    });

    const [selectedRows, setSelectedRows] = useState<number[]>(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved).selectedRows : [];
    });

    const rowsPerPage = 20;
    const navigate = useNavigate();

    useEffect(() => {
        if (Array.isArray(data)) {
            setSortConfig({ key: 'company_name', direction: 'asc' });
        } else {
            console.error('company data is not an array:', data);
        }
    }, [data]);

    useEffect(() => {
        const stateToSave: TableState = {
            sortConfig,
            currentPage,
            selectedRows,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }, [sortConfig, currentPage, selectedRows]);
    
    useEffect(() => {
        handleSelectRows(selectedRows);
    }, [selectedRows]);


    const handleCheckboxChange = (id: number) => {
        const index = selectedRows.indexOf(id);
        let updatedSelectedRows: number[];
        if (index === -1) {
            updatedSelectedRows = [...selectedRows, id];
        } else {
            updatedSelectedRows = selectedRows.filter(rowId => rowId !== id);
        }
        setSelectedRows(updatedSelectedRows);
    };

    const handleSelectAll = () => {
        const pageIds = paginatedData.map(row => row.id);
        setSelectedRows(prev => {
            // Add only rows not already selected
            const updatedSelection = [...prev, ...pageIds.filter(id => !prev.includes(id))];
            return updatedSelection;
        });
        handleSelectRows(pageIds);
    };

    const handleDeselectAll = () => {
        const pageIds = paginatedData.map(row => row.id);
        setSelectedRows(prev => prev.filter(id => !pageIds.includes(id))); // Remove only the current page IDs
        handleSelectRows([]);
    };

    const handleRowClick = (id: number) => {
        navigate(`/companies/detail/${id}`);
    };
    
    const handleDeleteClick = (row: Company) => {
        navigate(`/companies/delete/${row.id}`, { state: { rowData: row } });
    };

    const handleSortClick = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    // Derived sorted + paginated data instead of mutating companyData
    const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
        let aVal: string | number | null = '';
        let bVal: string | number | null = '';

        switch (sortConfig.key) {
        case 'company_name':
            aVal = a.legal_name || a.trade_name || '-';
            bVal = b.legal_name || b.trade_name || '-';
            break;
        case 'ubi':
            aVal = a.ubi ?? '';
            bVal = b.ubi ?? '';
            break;
        case 'industry':
            aVal = a.industry?.name ?? '';
            bVal = b.industry?.name ?? '';
            break;
        case 'total_interactions':
            aVal = a.total_interactions ?? 0;
            bVal = b.total_interactions ?? 0;
            break;
        case 'last_interaction_date':
            aVal = a.last_interaction_date ? Date.parse(a.last_interaction_date) : 0;
            bVal = b.last_interaction_date ? Date.parse(b.last_interaction_date) : 0;
            break;
        default:
            aVal = '';
            bVal = '';
        }

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1;

        if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        return 0;
    });

    return sorted;
    }, [data, sortConfig]);


    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '-';
        return `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCDate()).padStart(2, '0')}/${date.getUTCFullYear()}`;
    };

    const totalPages = Math.ceil(sortedData.length / rowsPerPage);
    const pageNumbersToShow = generatePageNumbers(totalPages, currentPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
    const paginatedData = sortedData.slice(startIndex, endIndex);

    return (
        <div>
        <div className="overflow-x-auto border border-solid border-titanium-white rounded-lg">
            <table className="min-w-full">
                <colgroup>
                    <col style={{ width: "6%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "17%" }} />
                </colgroup>
            <thead className="bg-spanish-gray text-white rounded-t-lg">
                <tr>
                <th className="px-4 py-2 text-left">
                    <input
                        type="checkbox" 
                        checked={paginatedData.every(row => selectedRows.includes(row.id))} // Select all on page
                        onChange={() =>
                            paginatedData.every(row => selectedRows.includes(row.id))
                                ? handleDeselectAll()
                                : handleSelectAll()
                        } 
                    />
                </th>
                {[
                    { key: 'company_name', label: 'Company Name', minWidth: 'min-w-64' },
                    { key: 'ubi', label: 'UBI', minWidth: 'min-w-40' },
                    { key: 'industry', label: 'Industry', minWidth: 'min-w-40' },
                    { key: 'total_interactions', label: 'Total Interactions', minWidth: 'min-w-36' },
                    { key: 'last_interaction_date', label: 'Last Interaction', minWidth: 'min-w-36' },
                ].map(({ key, label, minWidth }) => (
                    <th
                    key={key}
                    onClick={() => handleSortClick(key)}
                    className={`text-xs font-bold px-4 py-2 text-left ${minWidth} hover:cursor-pointer`}
                    >
                    <div className="flex items-center">
                        {label}
                        {sortConfig.key === key && (
                        <img
                            src={upArrow}
                            alt="sort arrow"
                            className={`ml-1 transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`}
                        />
                        )}
                    </div>
                    </th>
                ))}
                <th className="text-xs font-bold px-4 py-2 text-left">Manage</th>
                </tr>
            </thead>
            <tbody>
                {paginatedData.map((row) => (
                <tr
                    key={row.id}
                    onClick={() => handleRowClick(row.id)}
                    className="border-b border-solid border-titanium-white last:border-none h-10 hover:bg-at-white hover:text-black hover:cursor-pointer"
                >
                    <td className="px-4 py-2 text-gotham-gray text-sm text-left"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(row.id);
                        }}
                        >
                        <input
                            type="checkbox"
                            checked={selectedRows.includes(row.id)}
                            readOnly
                            className="pointer-events-none"
                        />
                    </td>
                    <td className="px-4 py-2 text-gotham-gray text-sm">{row.legal_name || row.trade_name || '-'}</td>
                    <td className="px-4 py-2 text-gotham-gray text-sm">{row.ubi || '-'}</td>
                    <td className="px-4 py-2 text-gotham-gray text-sm">{row.industry?.name || '-'}</td>
                    <td className="px-4 py-2 text-gotham-gray text-sm">{row.total_interactions ?? 0}</td>
                    <td className="px-4 py-2 text-gotham-gray text-sm">{formatDate(row.last_interaction_date ?? '-')}</td>
                    <td className="px-4 py-2">
                    <button
                        className="flex items-center"
                        onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(row);
                        }}
                    >
                        <img
                        className="border border-solid border-button-blue h-6 w-6 p-1 rounded-md"
                        src={deleteIcon}
                        alt="Delete"
                        />
                    </button>
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>

        {pageNumbersToShow.length > 1 && (
            <div className="flex justify-center mt-4">
            <div className="border border-solid border-at-white rounded-md flex">
                <button
                className="text-gotham-gray font-bold text-xs border-r border-solid border-at-white px-2 py-1"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                >
                « Previous
                </button>
                {pageNumbersToShow.map((pageNumber, index) => (
                <button
                    key={index}
                    className={`text-gotham-gray font-bold text-xs border-r border-solid border-at-white px-3 py-1 ${
                    pageNumber === currentPage ? 'bg-gray-300' : ''
                    }`}
                    onClick={() => setCurrentPage(typeof pageNumber === 'number' ? pageNumber : currentPage)}
                >
                    {pageNumber}
                </button>
                ))}
                <button
                className="text-gotham-gray font-bold text-xs px-2 py-1"
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                >
                Next »
                </button>
            </div>
            </div>
        )}
        </div>
    );
};

export default CompanyTable;
